/* ============================================================
   Ulmo Honey — homepage lab (Oct 22–23, 2026). Lives only in the "Homepage lab" theme.
   1 Living logo behind the homepage buy card. The Ulmo coil (the four gold rings from the
     logo, in its true proportions) sits oversized and softly blurred behind the card's text,
     running off the card's edges, and reads as a 3D object:
     - each ring is shaded for depth (near edge strong, far edge faint, like a hoop seen from
       slightly above), and where a ring crosses the one below, the lower ring passes behind
       it with a small gap either side, like hoops floating one above the other,
     - once the rings have drawn on, one shine orbits the whole hive slowly, left to right
       across the front and dimmer round the back, like a light circling a solid object,
     - the hive tilts in perspective toward the cursor (desktop) or with the scroll (phones),
       and turns very slowly on its own,
     - the rings breathe up and down a little, out of phase, like a spring,
     - a warm honey halo breathes behind it,
     - the first time the card scrolls into view the rings draw themselves on, then settle.
     CSS 3D transforms, opacity and filters (composited on the GPU) plus SVG gradients that turn.
     Reduced motion: the shaded coil and halo, still.
   2 Small ® in the card title, like the product page.
   3 Mailer icon on the product page's "Free shipping" badge.
   4 Product page gallery: about two seconds after the gallery is in view, the coverflow glides
     one photo to the left on its own, so the second photo ("more powerful than Manuka") comes
     into view without a swipe. Once per page view; any touch, drag, click or slide change on
     the gallery before then cancels it. Not under reduced motion.
   5 Health page, "Phenolic Content 2×" modal: the new 2× chart (the square product graphic) is
     shown cropped to its bars and source line, so the bars start exactly at the text's left
     edge and the chart's own title (which the modal already says) is left out.
   6 Homepage "Pure 100%" tile (was "Free of 0"): the tile's text and decoration are set in
     templates/index.json (label Pure, value 100 %, the spoon-of-honey photo pure-spoon-jar.webp as
     the decoration image); ulmo-lab.css places the photo like the flower on the 97% tile. No script.
     Tony passed on the three certification seals that were here first (Oct 23: "those seal
     placements aren't good", "can we think of something else").
   7 Product description: the page shows the short version (headline + three short paragraphs)
     with a Read more link on every screen, not only on phones; the detail sections written for
     AI shopping stay in the description (in <div class="u-desc-more">) for Google, the Shopify
     catalog and AI agents, and open on Read more.
   Earlier lab ideas Tony passed on: checkout stamp (Oct 22, parked in the project), jar picker
   with honey meter, live contour map of Volcán Puntiagudo, 3D honey pour (all Oct 23).
   ============================================================ */
(function () {
  'use strict';

  var d = document;
  var RM = !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);

  function ready(fn) { if (d.readyState !== 'loading') fn(); else d.addEventListener('DOMContentLoaded', fn); }
  function safe(name, fn) { try { fn(); } catch (e) { if (window.console) console.warn('[ulmo-lab] ' + name + ' skipped:', e && e.message); } }

  var MAILER_LINE = '<svg class="u-mailer-line" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true" focusable="false">' +
    '<path d="M12 2.75l8.5 4-8.5 4-8.5-4z"/><path d="M3.5 6.75v10l8.5 4 8.5-4v-10M12 10.75v10"/><path d="M7.75 4.75l8.5 4v10"/><path d="M5.6 10.4l4 1.88v4l-4-1.88z" stroke-width="1.1"/></svg>';

  /* ---------------- 1. Living logo ---------------- */
  /* The four rings of the Ulmo coil, measured from the 600 px logo file (logo_simple_square),
     in the logo's own coordinates. The view box crops to the rings. */
  var RINGS = [
    { cx: 298, cy: 100, rx: 85, ry: 40 },
    { cx: 298, cy: 139, rx: 101, ry: 50 },
    { cx: 298, cy: 177, rx: 110, ry: 58 },
    { cx: 298, cy: 218, rx: 115, ry: 66 }
  ];
  var VIEW = 'viewBox="170 50 256 240" aria-hidden="true" focusable="false"';
  function perimeter(r) { var a = r.rx, b = r.ry; return Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b))); }
  var seq = 0;

  /* Each ring is drawn as two arcs: the far arc (over the top) and the near arc (under the
     bottom), both running from the ring's left point to its right point. All far arcs are
     painted first and all near arcs on top, so where the near arc of a ring crosses the far arc
     of the ring below, the lower ring passes behind, and a mask cuts a small gap in the far arc
     either side of the crossing, like one hoop tucked under another. */
  var STROKE_SOFT = 6, STROKE_LIGHT = 2.4, GAP = 2.6;
  function arcs(r) {
    var l = (r.cx - r.rx) + ' ' + r.cy, rt = (r.cx + r.rx) + ' ' + r.cy, ab = 'A' + r.rx + ' ' + r.ry + ' 0 0 ';
    return { far: 'M' + l + ab + '1 ' + rt, near: 'M' + l + ab + '0 ' + rt };
  }
  /* the mask for ring i's far arc: white everywhere except along the near arcs of the rings
     above it (black, wider than the stroke, so a gap shows either side) */
  function underMask(id, i, width, cls) {
    var knocks = '';
    for (var j = 0; j < i; j++) {
      knocks += '<path class="' + cls + '" d="' + arcs(RINGS[j]).near + '" style="--len:' + (perimeter(RINGS[j]) / 2).toFixed(1) + ';--i:' + j + '"/>';
    }
    return '<mask id="' + id + '" maskUnits="userSpaceOnUse" x="170" y="50" width="256" height="240">' +
      '<rect x="170" y="50" width="256" height="240" fill="#fff"/>' +
      '<g fill="none" stroke="#000" stroke-width="' + width + '" stroke-linecap="round">' + knocks + '</g></mask>';
  }

  /* the blurred gold body of the coil. Each ring is shaded top to bottom: the far arc faint,
     the near arc strong. Each arc draws itself on when the card comes into view. */
  function softSVG() {
    var defs = '', far = '', near = '';
    RINGS.forEach(function (r, i) {
      var a = arcs(r), id = 'u-gold-' + seq + '-' + i, mid = 'u-under-' + seq + '-' + i;
      var style = ' style="--len:' + (perimeter(r) / 2).toFixed(1) + ';--i:' + i + '"';
      defs += '<linearGradient id="' + id + '" gradientUnits="userSpaceOnUse" x1="0" y1="' + (r.cy - r.ry) + '" x2="0" y2="' + (r.cy + r.ry) + '">' +
        '<stop offset="0" stop-color="#c9a24e" stop-opacity=".22"/><stop offset=".45" stop-color="#d9ae4f" stop-opacity=".55"/>' +
        '<stop offset="1" stop-color="#b8862f" stop-opacity="1"/></linearGradient>';
      if (i) defs += underMask(mid, i, STROKE_SOFT + 2 * GAP, 'u-glow__knock');
      far += '<path class="u-glow__ring" d="' + a.far + '" stroke="url(#' + id + ')"' + (i ? ' mask="url(#' + mid + ')"' : '') + style + '/>';
      near += '<path class="u-glow__ring" d="' + a.near + '" stroke="url(#' + id + ')"' + style + '/>';
    });
    return '<svg class="u-glow__coil u-glow__coil--soft" ' + VIEW + '><defs>' + defs + '</defs>' +
      '<g class="u-glow__far">' + far + '</g><g class="u-glow__near">' + near + '</g></svg>';
  }
  /* the shine: each ring's arcs are stroked with a gradient that is bright at one end only.
     The gradient is set up in a circle the size of the ring's width, squashed to the ring's
     height, and turned about the ring's center, so the bright arc travels round the ring at the
     pace of a point orbiting a hoop in 3D: quick across the front and back, slower at the
     sides. All four turn together, so it reads as one light circling the hive. It starts once
     the rings have drawn on (the script calls beginElement), from the left, moving right across
     the front (a negative turn in SVG, whose y axis points down). The far arcs are dimmer and
     pass behind the near arcs of the rings above, like the body. */
  var ORBIT_DUR = '13s', ORBIT_FROM = 180;
  function lightSVG() {
    var defs = '', far = '', near = '';
    RINGS.forEach(function (r, i) {
      var a = arcs(r), id = 'u-run-' + seq + '-' + i, mid = 'u-lunder-' + seq + '-' + i;
      var style = ' style="--i:' + i + '"';
      defs += '<linearGradient id="' + id + '" gradientUnits="userSpaceOnUse" x1="' + (-r.rx) + '" y1="0" x2="' + r.rx + '" y2="0" gradientTransform="translate(' + r.cx + ' ' + r.cy + ') scale(1 ' + (r.ry / r.rx).toFixed(4) + ') rotate(' + ORBIT_FROM + ')">' +
        '<stop offset="0" stop-color="#fff1c8" stop-opacity="0"/><stop offset=".64" stop-color="#fff1c8" stop-opacity="0"/>' +
        '<stop offset=".84" stop-color="#fff6dc" stop-opacity=".55"/><stop offset="1" stop-color="#fffaf0" stop-opacity="1"/>' +
        (RM ? '' : '<animateTransform class="u-glow__orbit" attributeName="gradientTransform" type="rotate" additive="sum" from="0" to="-360" dur="' + ORBIT_DUR + '" begin="indefinite" repeatCount="indefinite"/>') +
        '</linearGradient>';
      if (i) defs += underMask(mid, i, STROKE_LIGHT + 2 * GAP, 'u-glow__knock u-glow__knock--light');
      far += '<path class="u-glow__ring" d="' + a.far + '" stroke="url(#' + id + ')"' + (i ? ' mask="url(#' + mid + ')"' : '') + style + '/>';
      near += '<path class="u-glow__ring" d="' + a.near + '" stroke="url(#' + id + ')"' + style + '/>';
    });
    return '<svg class="u-glow__coil u-glow__coil--light" ' + VIEW + '><defs>' + defs + '</defs>' +
      '<g class="u-glow__far">' + far + '</g><g class="u-glow__near">' + near + '</g></svg>';
  }

  function livingLogo(card) {
    if (card.querySelector('.u-glow')) return;
    card.classList.add('u-buycard');
    seq++;
    var fine = !!(window.matchMedia && matchMedia('(hover: hover) and (pointer: fine)').matches);
    var wrap = d.createElement('div');
    wrap.className = 'u-glow' + (RM ? ' is-still is-in' : '');
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML = '<div class="u-glow__halo"><div class="u-glow__halo-core"></div></div>' +
      '<div class="u-glow__mark"><div class="u-glow__sway">' + softSVG() + lightSVG() + '</div></div>';
    card.insertBefore(wrap, card.firstChild);
    if (RM) return;

    var halo = wrap.querySelector('.u-glow__halo'), mark = wrap.querySelector('.u-glow__mark');
    /* one smooth easing for the tilt and drift: toward the pointer, or with the scroll on phones */
    var tx = 0, ty = 0, x = 0, y = 0, raf = 0, last = 0;
    function frame(now) {
      raf = 0;
      var dt = Math.min(0.05, (now - (last || now)) / 1000); last = now;
      var k = 1 - Math.exp(-dt * 3.2);
      x += (tx - x) * k; y += (ty - y) * k;
      mark.style.transform = 'translate(-50%, -50%) translate3d(' + (x * 8).toFixed(2) + 'px, ' + (y * 6).toFixed(2) + 'px, 0) rotateX(' + (-y * 7).toFixed(2) + 'deg) rotateY(' + (x * 10).toFixed(2) + 'deg)';
      halo.style.transform = 'translate(-50%, -50%) translate3d(' + (x * 24).toFixed(2) + 'px, ' + (y * 18).toFixed(2) + 'px, 0)';
      if (Math.abs(tx - x) > 0.001 || Math.abs(ty - y) > 0.001) raf = requestAnimationFrame(frame); else last = 0;
    }
    function kick() { if (!raf) raf = requestAnimationFrame(frame); }
    if (fine) {
      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
        ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
        wrap.classList.add('is-hover');
        kick();
      }, { passive: true });
      card.addEventListener('pointerleave', function () { tx = 0; ty = 0; wrap.classList.remove('is-hover'); kick(); }, { passive: true });
    } else {
      var onScroll = function () {
        var r = card.getBoundingClientRect();
        if (r.bottom < 0 || r.top > innerHeight) return;
        ty = Math.max(-1, Math.min(1, ((r.top + r.height / 2) / innerHeight - 0.5) * 2));
        kick();
      };
      addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
    /* draw on the first time the card is in view; the shine starts its orbit once the rings
       have drawn on (about 2.9 s: 2.2 s plus the last ring's delay), as the shine layer fades in */
    function reveal() {
      wrap.classList.add('is-in');
      setTimeout(function () {
        wrap.querySelectorAll('.u-glow__orbit').forEach(function (orbit) {
          if (orbit.beginElement) { try { orbit.beginElement(); } catch (e) { /* the shine stays where it is */ } }
        });
      }, 2900);
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) {
        if (es.some(function (e) { return e.intersectionRatio >= 0.35; })) { reveal(); io.disconnect(); }
      }, { threshold: [0.35] });
      io.observe(card);
    } else reveal();
  }

  /* ---------------- 2. Small superscript ® in the card title, like the product page's title ---------------- */
  function smallReg(card) {
    var h = card.querySelector('.fp-title-price-row h2');
    if (h && !h.querySelector('.u-reg') && h.innerHTML.indexOf('®') > -1) h.innerHTML = h.innerHTML.replace('®', '<sup class="u-reg">®</sup>');
  }

  function setupBuyCard() {
    d.querySelectorAll('.shopify-section[id*="__featured_product"] .image-with-text--text').forEach(function (card) {
      if (!card.querySelector('product-form')) return;
      safe('title mark', function () { smallReg(card); });
      safe('living logo', function () { livingLogo(card); });
    });
  }

  /* ---------------- 3. Mailer icon on the free-shipping badge ---------------- */
  function setupTrustIcon() {
    d.querySelectorAll('.u-pdp-trust-item').forEach(function (item) {
      if (!/free shipping/i.test(item.textContent)) return;
      var icon = item.querySelector('.u-pdp-trust-icon');
      if (icon && !icon.querySelector('.u-mailer-line')) icon.innerHTML = MAILER_LINE;
    });
  }

  /* ---------------- 4. Gallery nudge on the product page ---------------- */
  /* The coverflow is built by redesign-head.liquid once Swiper has loaded from its CDN, so wait
     for it (up to 10 s). Swiper keeps its instance on the element as el.swiper. Only the user's
     own actions cancel the nudge (Swiper's slideChange is not used: in loop mode it also fires
     on its own layout updates while the photos load). */
  function galleryNudge() {
    if (RM) return;
    var tries = 0;
    (function wait() {
      var el = d.querySelector('.u-pdp-coverflow.swiper');
      var sw = el && el.swiper;
      if (!sw) { if (++tries < 80) setTimeout(wait, 125); return; }
      var timer = 0, done = false;
      function cancel() { done = true; clearTimeout(timer); }
      el.addEventListener('pointerdown', cancel, { passive: true });
      el.addEventListener('touchstart', cancel, { passive: true });
      sw.on('touchStart', cancel);
      sw.on('keyPress', cancel);
      sw.on('navigationNext', cancel);
      sw.on('navigationPrev', cancel);
      function go() {
        if (done) return;
        done = true;
        if (sw.realIndex !== 0) return; /* the visitor has already moved on */
        try { sw.slideNext(1100); } catch (e) { /* leave the gallery as it is */ }
      }
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (es) {
          clearTimeout(timer);
          if (done) { io.disconnect(); return; }
          if (es.some(function (e) { return e.intersectionRatio >= 0.6; })) {
            timer = setTimeout(function () { io.disconnect(); go(); }, 2200);
          }
        }, { threshold: [0.6] });
        io.observe(el);
      } else timer = setTimeout(go, 2600);
    })();
  }

  /* ---------------- 5. Chart figure in the bento modals ---------------- */
  /* The theme opens a tile's modal by cloning the tile into .u-bento-modal__content, and the
     theme's stylesheet sizes any .u-bento-modal-image inside it (320 px tall, centered). For the
     2× phenolic chart we want crops instead, so when that image appears in a modal it is
     re-classed (so the theme's rules no longer match) and shown through one figure per window
     given below (percentages of the square image): the bars, flush with the text's left edge,
     and the FDA line and source right under them, without the chart's own empty band between. */
  var FIGURES = [
    { match: /2x-phenolic-content-of-manuka/i, windows: [
      { l: 6.3, r: 92.6, t: 34.5, b: 69.5 },
      { l: 6.3, r: 92.6, t: 86.8, b: 95.2 }
    ] }
  ];
  function figure(img, w, last) {
    var fig = d.createElement('div');
    fig.className = 'u-fig' + (last ? ' u-fig--last' : '');
    fig.style.cssText = '--l:' + w.l + ';--r:' + w.r + ';--t:' + w.t + ';--b:' + w.b;
    img.className = 'u-fig__img';
    img.removeAttribute('width'); img.removeAttribute('height');
    img.loading = 'eager';
    fig.appendChild(img);
    return fig;
  }
  function cropFigure(img) {
    var src = img.currentSrc || img.src || '';
    var f = null;
    for (var i = 0; i < FIGURES.length; i++) if (FIGURES[i].match.test(src)) { f = FIGURES[i]; break; }
    if (!f || img.closest('.u-fig')) return;
    var parent = img.parentNode, next = img.nextSibling;
    f.windows.forEach(function (w, i) {
      var fig = figure(i ? img.cloneNode(false) : img, w, i === f.windows.length - 1);
      if (i) fig.querySelector('img').setAttribute('alt', '');
      parent.insertBefore(fig, next);
    });
  }
  function setupModalFigures() {
    var contents = d.querySelectorAll('.u-bento-modal__content');
    if (!contents.length || !('MutationObserver' in window)) return;
    contents.forEach(function (content) {
      new MutationObserver(function () {
        content.querySelectorAll('img.u-bento-modal-image').forEach(function (img) { safe('figure', function () { cropFigure(img); }); });
      }).observe(content, { childList: true, subtree: true });
    });
  }

  /* ---------------- 6. Product description: short version + Read more on every screen ---------------- */
  /* The description's full text (the detail sections written for AI shopping, Oct 23) sits in a
     <div class="u-desc-more"> after the short version. The theme's own Read more button
     (redesign-pdp-sticky.js) is only shown on phones, and its stylesheet hides it elsewhere
     with a layered !important rule, so it is re-classed here (u-lab-toggle) and styled by
     ulmo-lab.css on all screens; ulmo-lab.css also hides .u-desc-more until it is expanded.
     If the theme's button isn't there, an identical one is made. Nothing changes for a
     description without .u-desc-more. */
  function descCollapse() {
    var desc = d.querySelector('.product__info-container .product-description-standard');
    if (!desc || !desc.querySelector('.u-desc-more')) return;
    var tries = 0;
    (function wait() {
      var btn = desc.parentNode.querySelector('.u-desc-toggle, .u-lab-toggle');
      if (!btn) {
        if (++tries < 30) { setTimeout(wait, 100); return; }
        if (!desc.id) desc.id = 'productDescriptionTxt';
        btn = d.createElement('button');
        btn.type = 'button';
        btn.setAttribute('aria-controls', desc.id);
        btn.setAttribute('aria-expanded', 'false');
        btn.textContent = 'Read more';
        desc.insertAdjacentElement('afterend', btn);
        btn.addEventListener('click', function () {
          var open = desc.classList.toggle('is-expanded');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          btn.textContent = open ? 'Read less' : 'Read more';
        });
      }
      desc.classList.add('u-desc-collapsible');
      btn.classList.remove('u-desc-toggle');
      btn.classList.add('u-lab-toggle');
      /* the theme gives every paragraph 10 px below (a layered !important rule), so while the
         detail is hidden the short version's last paragraph gives that up and the button sits
         8 px under the text, as on phones today */
      var lastShort = desc.querySelector('.u-desc-more').previousElementSibling;
      function gap() {
        if (!lastShort) return;
        if (desc.classList.contains('is-expanded')) lastShort.style.removeProperty('margin-bottom');
        else lastShort.style.setProperty('margin-bottom', '0', 'important');
      }
      gap();
      btn.addEventListener('click', function () { setTimeout(gap, 0); });
    })();
  }

  ready(function () {
    safe('buy card', setupBuyCard);
    safe('trust icon', setupTrustIcon);
    safe('gallery nudge', galleryNudge);
    safe('modal figures', setupModalFigures);
    safe('description', descCollapse);
  });
})();
